import { authClient } from '../auth/client.ts'
import { serverUrl } from '../platform/origin.ts'

/** The admin endpoints of the Node server (ARCHITECTURE 6.4 layer 6; M22: the server's own auth module and file store, docs/STACK.md 8.4): the part of an
 *  administrator's action that the database alone cannot do. Called with the person's own access token (authClient, refreshed when it is about to expire);
 *  the server asks the database whether they may, and has just proved a second factor.
 *  The server is at the club's PUBLIC origin (platform/origin.ts): the page's own in a browser, the configured one in the native app, whose page
 *  origin is the shell's (M19b). A native build made without one cannot reach it and says so (NO_PUBLIC_ORIGIN), it never calls the shell. */
export class AdminError extends Error { constructor(public code: string, public status: number) { super(code) } }

function adminUrl(path: string): string {
  const url = serverUrl(path)
  if (!url) throw new AdminError('NO_PUBLIC_ORIGIN', 0)
  return url
}

export async function postAdmin<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const token = await authClient.accessToken()
  if (!token) throw new AdminError('NOT_AUTHENTICATED', 401)
  const r = await fetch(adminUrl(path), { method: 'POST', headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` }, body: JSON.stringify(body) }).catch(() => { throw new AdminError('NETWORK', 0) })
  const j = (await r.json().catch(() => ({}))) as { error?: string }
  if (!r.ok) throw new AdminError(j.error ?? 'UNKNOWN', r.status)
  return j as T
}

/** An admin endpoint that answers with a FILE (the club's export): the same token and the same refusals, then the bytes, the name the server chose, and the id of the trace it wrote. */
export async function postAdminDownload(path: string, body: Record<string, unknown>): Promise<{ blob: Blob; filename: string; exportId: string | null }> {
  const token = await authClient.accessToken()
  if (!token) throw new AdminError('NOT_AUTHENTICATED', 401)
  const r = await fetch(adminUrl(path), { method: 'POST', headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` }, body: JSON.stringify(body) }).catch(() => { throw new AdminError('NETWORK', 0) })
  if (!r.ok) { const j = (await r.json().catch(() => ({}))) as { error?: string }; throw new AdminError(j.error ?? 'UNKNOWN', r.status) }
  const filename = /filename="([A-Za-z0-9._-]{1,120})"/.exec(r.headers.get('content-disposition') ?? '')?.[1] ?? 'gk-export.zip'
  return { blob: await r.blob(), filename, exportId: r.headers.get('x-export-id') }
}

/** Hand a file to the browser's download (nothing is uploaded or kept). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.rel = 'noopener'
  document.body.append(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

/** The link an administrator gives a person (or a workstation): the one-time token rides in the URL FRAGMENT, so it is never sent to any server log. */
export const accessLink = (origin: string, kind: 'accept' | 'kiosk', tokenHash: string, email: string) =>
  `${origin}/${kind === 'accept' ? 'accept' : 'kiosk/enrol'}#t=${encodeURIComponent(tokenHash)}&e=${encodeURIComponent(email)}`
